/**
 * Tiny custom cookie-consent + analytics gating.
 *
 *  - Plausible (cookieless, already loaded in index.html) — always on, no consent.
 *  - Microsoft Clarity (heatmaps + session recordings) — uses cookies, requires consent.
 *    Only activates AFTER the user clicks "Accept analytics" in the banner.
 *
 * Project ID is supplied via REACT_APP_CLARITY_PROJECT_ID in /app/frontend/.env.
 * If unset, Clarity is silently disabled (and the consent banner still appears
 * so visitors can choose, but the choice has no immediate effect — useful for
 * later when you add Clarity or other cookie-based tools).
 */

const STORAGE_KEY = "ekselens.cookie.consent";
const VALID = new Set(["accepted", "rejected"]);

export const getConsent = () => {
  try {
    const v = window.localStorage.getItem(STORAGE_KEY);
    return VALID.has(v) ? v : null;
  } catch (e) {
    if (typeof console !== "undefined") console.warn("[consent] get failed:", e);
    return null;
  }
};

export const setConsent = (value) => {
  if (!VALID.has(value)) return;
  try {
    window.localStorage.setItem(STORAGE_KEY, value);
  } catch (e) {
    // Private-browsing mode / quota — analytics consent won't persist this
    // session but the user can re-grant on next visit.
    if (typeof console !== "undefined") console.warn("[consent] set failed:", e);
  }
  if (value === "accepted") {
    loadClarity();
  }
};

let clarityLoaded = false;

export const loadClarity = () => {
  if (clarityLoaded) return;
  const projectId = process.env.REACT_APP_CLARITY_PROJECT_ID;
  if (!projectId) return;
  clarityLoaded = true;
  // Standard Microsoft Clarity snippet (from clarity.microsoft.com -> Settings -> Install)
  (function (c, l, a, r, i, t, y) {
    c[a] =
      c[a] ||
      function () {
        (c[a].q = c[a].q || []).push(arguments);
      };
    t = l.createElement(r);
    t.async = 1;
    t.src = "https://www.clarity.ms/tag/" + i;
    y = l.getElementsByTagName(r)[0];
    y.parentNode.insertBefore(t, y);
  })(window, document, "clarity", "script", projectId);
};

/** Bootstrap on app load — if the user previously accepted, fire Clarity immediately. */
export const initConsent = () => {
  if (getConsent() === "accepted") loadClarity();
};
